import { supabase, getAccessToken } from "./supabase";

// live_games / live_game_players used to be read and written directly from
// the browser to Supabase (via supabaseTable, bypassing /api/supabase-data)
// because anonymous players joining via QR code aren't signed-in Supabase
// users. That direct browser-to-Supabase path proved unreliable in
// production (CORS/connectivity failures that had nothing to do with auth
// or RLS -- both were verified correct server-side). Routed through this
// same-origin endpoint instead, which sidesteps that path entirely.
// Realtime (broadcast, postgres_changes) stays on the direct WebSocket
// connection below -- only the REST/fetch path was affected.
const LIVE_GAME_ENDPOINT = "/api/live-game";
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
const LIVE_GAME_RETRY_DELAYS_MS = [600, 1200, 2400];
// This fires on every player join/leave/answer/rename during a live session
// and used to have no timeout -- a hang here (a slow function, a downstream
// auth check stuck on a degraded Supabase endpoint) never reached the retry
// loop below at all, it just hung the calling await forever. Bounding it and
// treating a timeout as retryable (like the existing 5xx handling) lets the
// same backoff/retry logic actually kick in instead.
const LIVE_GAME_TIMEOUT_MS = 8000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const retryDelay = (attempt) => LIVE_GAME_RETRY_DELAYS_MS[Math.min(attempt, LIVE_GAME_RETRY_DELAYS_MS.length - 1)] + Math.floor(Math.random() * 180);

const postLiveGame = async (action, params) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LIVE_GAME_TIMEOUT_MS);
  try {
    const response = await fetch(LIVE_GAME_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({ action, ...params }),
    });
    const result = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, data: result?.data, error: result?.error, retryable: Boolean(result?.retryable) };
  } catch (error) {
    const timedOut = error?.name === "AbortError";
    return { ok: false, status: 0, data: undefined, error: timedOut ? "Live game request timed out" : (error?.message || "Live game request failed"), retryable: true };
  } finally {
    clearTimeout(timeoutId);
  }
};

const callLiveGame = async (action, params = {}) => {
  const result = await postLiveGame(action, params);
  if (result.ok) return result.data;
  throw new Error(result.error || `Live game request failed (${result.status})`);
};

// Host-only actions authenticate with the same cached, auto-refreshing
// session lookup the data proxy uses. A first attempt with a stale cached
// token that the server rejects (401) retries once with a forced refresh.
// Transient proxy/Supabase auth failures (503/5xx) retry with backoff because
// they are not sign-in failures and should not interrupt a live show.
const callAuthedLiveGame = async (action, params = {}) => {
  let authToken = await getAccessToken();
  let result = await postLiveGame(action, { ...params, authToken });

  if (!result.ok && result.status === 401) {
    const refreshedToken = await getAccessToken(true);
    if (refreshedToken && refreshedToken !== authToken) {
      authToken = refreshedToken;
      result = await postLiveGame(action, { ...params, authToken });
    }
  }

  for (let attempt = 0; !result.ok && (result.retryable || RETRYABLE_STATUSES.has(result.status)) && attempt < LIVE_GAME_RETRY_DELAYS_MS.length; attempt++) {
    await sleep(retryDelay(attempt));
    result = await postLiveGame(action, { ...params, authToken });
  }

  if (result.ok) return result.data;
  throw new Error(result.error || `Live game request failed (${result.status})`);
};

// Read-only lookup for players/presentation screens: they can't create a
// live_games row (host-only), so they wait for one to exist.
export const findLiveGame = (sessionId) => callLiveGame("findLiveGame", { sessionId });

// Host-only: reuses the session's current live game if one exists, so a
// host reload doesn't orphan the roster from a fresh row. Test runs
// (isTest: true) get their own row scoped separately from the real game --
// see ensureLiveGame in frontend/api/live-game.js for how going live for
// real clears out a leftover test row.
export const ensureLiveGame = (sessionId, { sessionName, isTest } = {}) => callAuthedLiveGame("ensureLiveGame", { sessionId, sessionName, isTest });

// Host-only: wipes any prior test game (and its teams/scores/answers, via
// cascade) for this session. Call once when the host explicitly starts a
// Test Run, before ensureLiveGame -- not on every host-page load, which
// would erase an in-progress test roster on unrelated re-renders.
export const resetTestGame = (sessionId) => callAuthedLiveGame("resetTestGame", { sessionId });

export const fetchLivePlayers = (gameId) => callLiveGame("fetchLivePlayers", { gameId });

// Omits `score` unless explicitly passed, so joining/renaming never resets
// a player's existing score back to 0 on conflict.
export const upsertLivePlayer = (gameId, player) => callLiveGame("upsertPlayer", { gameId, player });

export const setLivePlayerName = (playerId, name) => callLiveGame("setPlayerName", { playerId, name });

export const removeLivePlayer = (playerId) => callAuthedLiveGame("removeLivePlayer", { playerId });

// Roster/leaderboard changes ride their own realtime subscription on
// live_game_players instead of the host_state broadcast, so a player
// joining or a score changing no longer forces a full question-object
// rebroadcast to every connected player.
export const subscribeLivePlayers = (gameId, onChange) => {
  const channel = supabase
    .channel(`live-game-players-${gameId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "live_game_players", filter: `game_id=eq.${gameId}` }, onChange)
    .subscribe();
  return () => supabase.removeChannel(channel);
};
